import {
  MyButton,
  MyTextField,
  MyAutocomplete,
  MyDropzone,
  MyTextArea,
} from '@interstellar-component'
import { ArrowLeft, Mail01 } from '@untitled-ui/icons-react'
import { useApp } from '@src/AppContext'
import { useRegisterCompanyInfo } from '../Context'

function StepCompanyInfo({
  control,
  errors,
  trigger,
  watch,
  setValue,
  isSubmitting,
  onSaveDraft,
  onBackToCompanyMenu,
}) {
  const { options, loadCities, loadDistricts, loadSubdistricts, loadPostalCodes } =
    useRegisterCompanyInfo()
  const { companyContext } = useApp()
  // A first-time tenant reaches this wizard because it has NO company yet
  // (count === 0), and /company auto-redirects such a tenant straight back here
  // — so "Back to company menu" is a dead no-op for them. Hide it when there is
  // genuinely no company menu to return to (Everest #z94x8e24fw). A draft-resume
  // tenant (count >= 1) keeps the working button.
  const hasCompanyMenu = (companyContext?.count ?? 0) > 0
  const {
    company_name,
    business_category,
    industry,
    company_size,
    website,
    email,
    province,
    city,
    district,
    subdistrict,
    postal_code,
    company_address,
    logo,
  } = watch()

  // Cascading address selects: picking a parent clears its descendants and
  // loads the next level from /v1/geographic/*.
  const clearFields = (fields) => fields.forEach((f) => setValue(f, null, { shouldDirty: true }))

  return (
    <div className="flex flex-col w-full max-w-[1000px] mx-auto py-8 pb-12 gap-8">
      {/* Header section */}
      <div className="flex flex-col gap-6 w-full border-b border-gray-200 pb-6">
        <div className="px-8 w-full flex flex-col gap-5">
          {hasCompanyMenu ? (
            <button
              type="button"
              onClick={onBackToCompanyMenu}
              className="flex items-center gap-1.5 text-brand/700 hover:text-brand/800 font-semibold text-sm w-fit transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-brand/700" /> Back to company menu
            </button>
          ) : null}
          <div className="flex flex-col gap-1 w-full">
            <h1 className="display-xs-semibold text-gray-900">Company Information</h1>
            <p className="text-md-regular text-gray-600">
              Provide basic information about your company to set up your account.
            </p>
          </div>
        </div>
      </div>

      {/* Section */}
      <div className="flex flex-col gap-8 w-full">
        <div className="px-8 w-full flex flex-col gap-6">
          {/* Form Area 1: Company Details */}
          <div className="flex flex-col xl:flex-row gap-8 w-full">
            {/* Section label */}
            <div className="w-full xl:w-[280px] shrink-0 flex flex-col">
              <h2 className="text-sm-semibold text-gray-900">Company Details</h2>
              <p className="text-sm-regular text-gray-600">Basic information about your company.</p>
            </div>

            {/* Form panel */}
            <div className="flex-1 max-w-[624px] rounded-xl border border-gray-200 shadow-[0_1px_2px_rgba(10,13,18,0.05)] bg-white flex flex-col">
              <div className="p-6 flex flex-col gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm-medium text-gray-700 after:text-brand/900 after:content-['*']">
                    Legal name
                  </label>
                  <MyTextField
                    name="company_name"
                    trigger={trigger}
                    placeholder="e.g. PT Everest Maju Sejahtera"
                    control={control}
                    value={company_name}
                    errors={errors?.company_name?.message}
                  />
                </div>

                <section className="w-full flex flex-col md:flex-row justify-between gap-6 ">
                  <div className="flex flex-col gap-1.5 w-full">
                    <label className="text-sm-medium text-gray-700 after:text-brand/900 after:content-['*']">
                      Business category
                    </label>
                    <MyAutocomplete
                      name="business_category"
                      options={options.businessCategories}
                      trigger={trigger}
                      placeholder="Select type"
                      control={control}
                      value={business_category}
                      error={errors?.business_category?.message}
                      onChange={(_e, value) => setValue('business_category', value, { shouldDirty: true })}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5 w-full">
                    <label className="text-sm-medium text-gray-700 after:text-brand/900 after:content-['*']">
                      Industry
                    </label>
                    <MyAutocomplete
                      name="industry"
                      options={options.industries}
                      trigger={trigger}
                      placeholder="Select industry"
                      control={control}
                      value={industry}
                      error={errors?.industry?.message}
                      onChange={(_e, value) => setValue('industry', value, { shouldDirty: true })}
                    />
                  </div>
                </section>

                <div className="flex flex-col gap-1.5 max-w-[312px]">
                  <label className="text-sm-medium text-gray-700 after:text-brand/900 after:content-['*']">
                    Number of employee
                  </label>
                  <MyAutocomplete
                    name="company_size"
                    options={options.employeeRanges}
                    trigger={trigger}
                    placeholder="Select range"
                    control={control}
                    value={company_size}
                    error={errors?.company_size?.message}
                    onChange={(_e, value) => setValue('company_size', value, { shouldDirty: true })}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm-medium text-gray-700">Company Website</label>
                  <MyTextField
                    name="website"
                    trigger={trigger}
                    placeholder="e.g. www.everest.co.id"
                    control={control}
                    value={website}
                    errors={errors?.website?.message}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm-medium text-gray-700 after:text-brand/900 after:content-['*']">
                    Email address
                  </label>
                  <MyTextField
                    name="email"
                    trigger={trigger}
                    placeholder="e.g. olivia@everest.co.id"
                    control={control}
                    value={email}
                    errors={errors?.email?.message}
                    startAdornment={<Mail01 className="w-5 h-5 text-gray-500" />}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm-medium text-gray-700 after:text-brand/900 after:content-['*']">
                    Logo
                  </label>
                  <MyDropzone
                    colorBg="bg-white"
                    multiple={false}
                    accept={['.png', '.jpg', '.jpeg', '.svg', '.gif']}
                    maxSize={5242880} // 5MB
                    showImage
                    // Seed from the persisted form value so the uploaded logo's
                    // chip survives leaving/returning to this step (the wizard
                    // unmounts inactive steps) — Everest #86exxpv9k.
                    value={logo instanceof File ? [logo] : []}
                    onChange={(files) => {
                      setValue('logo', files && files.length > 0 ? files[0] : null, {
                        shouldDirty: true,
                      })
                    }}
                    errors={errors?.logo?.message}
                  />
                </div>
              </div>
            </div>
          </div>

          <hr className="w-full border-gray-200" />

          {/* Form Area 2: Company Address */}
          <div className="flex flex-col xl:flex-row gap-8 w-full">
            {/* Section label */}
            <div className="w-full xl:w-[280px] shrink-0 flex flex-col">
              <h2 className="text-sm-semibold text-gray-900">Company Address</h2>
              <p className="text-sm-regular text-gray-600">
                Location details of your registered company address.
              </p>
            </div>

            {/* Form panel */}
            <div className="flex-1 max-w-[624px] rounded-xl border border-gray-200 shadow-[0_1px_2px_rgba(10,13,18,0.05)] bg-white flex flex-col">
              <div className="p-6 flex flex-col gap-5">
                <section className="w-full flex flex-col md:flex-row justify-between gap-6">
                  <div className="flex flex-col gap-1.5 w-full">
                    <label className="text-sm-medium text-gray-700 after:text-brand/900 after:content-['*']">
                      Province
                    </label>
                    <MyAutocomplete
                      name="province"
                      options={options.provinces}
                      trigger={trigger}
                      placeholder="Select Province"
                      control={control}
                      value={province}
                      error={errors?.province?.message}
                      onChange={(_e, value) => {
                        setValue('province', value, { shouldDirty: true })
                        clearFields(['city', 'district', 'subdistrict', 'postal_code'])
                        loadCities(value?.id)
                      }}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5 w-full">
                    <label className="text-sm-medium text-gray-700 after:text-brand/900 after:content-['*']">
                      City / Regency
                    </label>
                    <MyAutocomplete
                      name="city"
                      options={options.cities}
                      trigger={trigger}
                      placeholder="Select City / Regency"
                      control={control}
                      value={city}
                      error={errors?.city?.message}
                      onChange={(_e, value) => {
                        setValue('city', value, { shouldDirty: true })
                        clearFields(['district', 'subdistrict', 'postal_code'])
                        loadDistricts(value?.id)
                      }}
                    />
                  </div>
                </section>

                <section className="w-full flex flex-col md:flex-row justify-between gap-6 ">
                  <div className="flex flex-col gap-1.5 w-full">
                    <label className="text-sm-medium text-gray-700 after:text-brand/900 after:content-['*']">
                      District
                    </label>
                    <MyAutocomplete
                      name="district"
                      options={options.districts}
                      trigger={trigger}
                      placeholder="Select District"
                      control={control}
                      value={district}
                      error={errors?.district?.message}
                      onChange={(_e, value) => {
                        setValue('district', value, { shouldDirty: true })
                        clearFields(['subdistrict', 'postal_code'])
                        loadSubdistricts(value?.id)
                      }}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5 w-full">
                    <label className="text-sm-medium text-gray-700 after:text-brand/900 after:content-['*']">
                      Subdistrict
                    </label>
                    <MyAutocomplete
                      name="subdistrict"
                      options={options.subdistricts}
                      trigger={trigger}
                      placeholder="Select Subdistrict"
                      control={control}
                      value={subdistrict}
                      error={errors?.subdistrict?.message}
                      onChange={(_e, value) => {
                        setValue('subdistrict', value, { shouldDirty: true })
                        clearFields(['postal_code'])
                        loadPostalCodes(value?.id)
                      }}
                    />
                  </div>
                </section>

                <div className="flex flex-col gap-1.5 max-w-[312px]">
                  <label className="text-sm-medium text-gray-700 after:text-brand/900 after:content-['*']">
                    Postal Code
                  </label>
                  <MyAutocomplete
                    name="postal_code"
                    options={options.postalCodes}
                    trigger={trigger}
                    placeholder="Select Postal Code"
                    control={control}
                    value={postal_code}
                    error={errors?.postal_code?.message}
                    onChange={(_e, value) => setValue('postal_code', value, { shouldDirty: true })}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm-medium text-gray-700 after:text-brand/900 after:content-['*']">
                    Company Address
                  </label>
                  <MyTextArea
                    name="company_address"
                    trigger={trigger}
                    placeholder="e.g. Kawasan Rasuna Epicentrum, JL. HR. Rasuna Said"
                    control={control}
                    value={company_address}
                    errors={errors?.company_address?.message}
                    rows={4}
                  />
                  {/* Live counter (was a hardcoded "275 characters left" that
                      never moved). 275 is the server cap (Everest #86ey2n7a2). */}
                  <p
                    className={`text-sm-regular mt-1 ${
                      (company_address?.length || 0) > 275 ? 'text-error/600' : 'text-gray-500'
                    }`}
                  >
                    {Math.max(0, 275 - (company_address?.length || 0))} characters left
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-8 w-full">
          <hr className="w-full border-gray-200 mb-5" />
          <div className="flex justify-end w-full gap-3">
            <MyButton
              variant="outlined"
              color="secondary"
              type="button"
              size="md"
              disabled={isSubmitting}
              onClick={onSaveDraft}
            >
              <span className="text-sm-semibold">Cancel & save draft</span>
            </MyButton>
            <MyButton
              size="md"
              variant="filled"
              color="primary"
              type="submit"
              disabled={isSubmitting}
            >
              <span className="text-sm-semibold text-white">Next</span>
            </MyButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StepCompanyInfo
